import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { fetchJobApplicants, updateApplicationStatus } from '../services/api';
import { format } from 'date-fns';

const JobApplicantsModal = ({ job, onClose }) => {
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [filters, setFilters] = useState({
    applicationStatus: '',
    firstName: '',
    lastName: '',
    applicantEmail: '',
    availability: '',
    minYearsOfExperience: '',
    maxYearsOfExperience: '',
    hasResume: '',
    sortBy: 'appliedAt',
    sortDirection: 'desc'
  });

  const statusOptions = [
    { value: 'PENDING', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'REVIEWED', label: 'Reviewed', color: 'bg-green-100 text-green-800' },
    { value: 'SHORTLISTED', label: 'Shortlisted', color: 'bg-blue-100 text-blue-800' },
    { value: 'REJECTED', label: 'Rejected', color: 'bg-red-100 text-red-800' },
    { value: 'HIRED', label: 'Hired', color: 'bg-cyan-100 text-cyan-800' },
    { value: 'HOLD', label: 'On Hold', color: 'bg-orange-100 text-orange-800' },
    { value: 'OFFER', label: 'Offer Sent', color: 'bg-purple-100 text-purple-800' }
  ];

  useEffect(() => {
    if (job) {
      fetchApplicantsData();
    }
  }, [job, page, filters]);

  const fetchApplicantsData = async () => {
    if (!job) return;
    
    setLoading(true);
    try {
      const params = {
        ...filters,
        page,
        size: 10
      };
      
      const data = await fetchJobApplicants(job.id, params);
      setApplicants(data.content || []);
      setTotalPages(data.totalPages || 0);
      setTotalElements(data.totalElements || 0);
    } catch (error) {
      console.error('Error fetching job applicants:', error);
      toast.error('Failed to fetch job applicants');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      applicationStatus: '',
      firstName: '',
      lastName: '',
      applicantEmail: '',
      availability: '',
      minYearsOfExperience: '',
      maxYearsOfExperience: '',
      hasResume: '',
      sortBy: 'appliedAt',
      sortDirection: 'desc'
    };
    setFilters(clearedFilters);
    setPage(0);
  };

  const handleStatusUpdate = async (applicationId, newStatus) => {
    setUpdatingStatus(prev => ({ ...prev, [applicationId]: true }));
    
    try {
      await updateApplicationStatus(applicationId, newStatus, `Status updated to ${newStatus}`);
      
      // Update local state
      setApplicants(prev => 
        prev.map(app => 
          app.applicationId === applicationId 
            ? { ...app, applicationStatus: newStatus }
            : app
        )
      );
      
      toast.success('Application status updated successfully');
    } catch (error) {
      console.error('Error updating application status:', error);
      toast.error('Failed to update application status');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [applicationId]: false }));
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch (error) {
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    const statusOption = statusOptions.find(option => option.value === status);
    return statusOption ? statusOption.color : 'bg-gray-100 text-gray-800';
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-7xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                  Job Applicants - {job?.title}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Total: {totalElements} applicants
                </p>

                {/* Filter Toggle */}
                <div className="mt-4 flex justify-between items-center">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {showFilters ? 'Hide Filters' : 'Show Filters'}
                  </button>
                </div>

                {/* Filters */}
                {showFilters && (
                  <div className="mt-4 bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {/* Application Status */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Status</label>
                        <select
                          value={filters.applicationStatus}
                          onChange={(e) => handleFilterChange('applicationStatus', e.target.value)}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        >
                          <option value="">All Statuses</option>
                          {statusOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* First Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">First Name</label>
                        <input
                          type="text"
                          value={filters.firstName}
                          onChange={(e) => handleFilterChange('firstName', e.target.value)}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                          placeholder="Search by first name"
                        />
                      </div>

                      {/* Last Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Last Name</label>
                        <input
                          type="text"
                          value={filters.lastName}
                          onChange={(e) => handleFilterChange('lastName', e.target.value)}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                          placeholder="Search by last name"
                        />
                      </div>

                      {/* Email */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <input
                          type="email"
                          value={filters.applicantEmail}
                          onChange={(e) => handleFilterChange('applicantEmail', e.target.value)}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                          placeholder="Search by email"
                        />
                      </div>

                      {/* Experience Range */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Min Experience (Years)</label>
                        <input
                          type="number"
                          value={filters.minYearsOfExperience}
                          onChange={(e) => handleFilterChange('minYearsOfExperience', e.target.value)}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                          placeholder="0"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Max Experience (Years)</label>
                        <input
                          type="number"
                          value={filters.maxYearsOfExperience}
                          onChange={(e) => handleFilterChange('maxYearsOfExperience', e.target.value)}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                          placeholder="20"
                        />
                      </div>

                      {/* Has Resume */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Resume Status</label>
                        <select
                          value={filters.hasResume}
                          onChange={(e) => handleFilterChange('hasResume', e.target.value)}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        >
                          <option value="">All</option>
                          <option value="true">Has Resume</option>
                          <option value="false">No Resume</option>
                        </select>
                      </div>

                      {/* Availability */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Availability</label>
                        <input
                          type="text"
                          value={filters.availability}
                          onChange={(e) => handleFilterChange('availability', e.target.value)}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                          placeholder="e.g., 2 weeks, immediate"
                        />
                      </div>
                    </div>

                    <div className="mt-4 flex justify-end space-x-2">
                      <button
                        onClick={handleClearFilters}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Clear Filters
                      </button>
                    </div>
                  </div>
                )}

                {/* Applicants List */}
                <div className="mt-6">
                  {loading ? (
                    <div className="flex justify-center items-center py-8">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    </div>
                  ) : applicants.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No applicants found for this job.</p>
                    </div>
                  ) : (
                    <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-300">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Applicant
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Experience
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Salary
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Applied Date
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {applicants.map((applicant) => (
                            <tr key={applicant.applicationId}>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0 h-10 w-10">
                                    <img
                                      className="h-10 w-10 rounded-full"
                                      src={applicant.profileImageUrl || '/api/placeholder/40/40'}
                                      alt=""
                                    />
                                  </div>
                                  <div className="ml-4">
                                    <div className="text-sm font-medium text-gray-900">
                                      {applicant.firstName} {applicant.lastName}
                                    </div>
                                    <div className="text-sm text-gray-500">{applicant.email}</div>
                                    <div className="text-sm text-gray-500">{applicant.phoneNumber}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{applicant.yearsOfExperience} years</div>
                                <div className="text-sm text-gray-500">Available: {applicant.availability}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">Current: {applicant.currentSalary}</div>
                                <div className="text-sm text-gray-500">Expected: {applicant.expectedSalary}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <select
                                  value={applicant.applicationStatus}
                                  onChange={(e) => handleStatusUpdate(applicant.applicationId, e.target.value)}
                                  disabled={updatingStatus[applicant.applicationId]}
                                  className={`text-xs font-semibold rounded-full px-2 py-1 ${getStatusColor(applicant.applicationStatus)} border-0 focus:ring-2 focus:ring-blue-500`}
                                >
                                  {statusOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatDate(applicant.appliedAt)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex space-x-2">
                                  {applicant.resumeUrl && (
                                    <a
                                      href={applicant.resumeUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:text-blue-900"
                                    >
                                      View Resume
                                    </a>
                                  )}
                                  {applicant.coverLetter && (
                                    <button
                                      onClick={() => alert(applicant.coverLetter)}
                                      className="text-green-600 hover:text-green-900"
                                    >
                                      Cover Letter
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Pagination */}
                  {!loading && applicants.length > 0 && totalPages > 1 && (
                    <div className="mt-6 flex items-center justify-between">
                      <div className="flex-1 flex justify-between sm:hidden">
                        <button
                          onClick={() => handlePageChange(page - 1)}
                          disabled={page === 0}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => handlePageChange(page + 1)}
                          disabled={page >= totalPages - 1}
                          className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm text-gray-700">
                            Showing <span className="font-medium">{page * 10 + 1}</span> to{' '}
                            <span className="font-medium">{Math.min((page + 1) * 10, totalElements)}</span> of{' '}
                            <span className="font-medium">{totalElements}</span> results
                          </p>
                        </div>
                        <div>
                          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                            <button
                              onClick={() => handlePageChange(page - 1)}
                              disabled={page === 0}
                              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Previous
                            </button>
                            {[...Array(totalPages)].map((_, i) => (
                              <button
                                key={i}
                                onClick={() => handlePageChange(i)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                  i === page
                                    ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                }`}
                              >
                                {i + 1}
                              </button>
                            ))}
                            <button
                              onClick={() => handlePageChange(page + 1)}
                              disabled={page >= totalPages - 1}
                              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Next
                            </button>
                          </nav>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobApplicantsModal; 